import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, FileSearch, CalendarCheck, Wallet, ShieldCheck, Sparkles, LineChart, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { landingPathFor } from "@/lib/device";
import { Wordmark } from "@/components/marketing/Wordmark";
import { BuiltOSMark } from "@/components/common/BuilderLogo";

const VALUE_PROPS = [
  { icon: FileSearch, title: "AI plan take-offs", desc: "Upload plans, get quantity take-offs and AUD estimates in minutes." },
  { icon: CalendarCheck, title: "Scheduling & deliveries", desc: "Gantt with predecessors, subbie bookings and material deliveries." },
  { icon: Wallet, title: "Money, watched", desc: "POs, progress claims, 12-week cashflow forecast — synced with Xero." },
];

const PROOF_POINTS = [
  { icon: Sparkles, label: "AI estimating" },
  { icon: LineChart, label: "Live Xero sync" },
  { icon: ShieldCheck, label: "GST-aware · AUD" },
];

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let landedUser = null;
      if (mode === "login") {
        landedUser = await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        if (!form.name.trim()) {
          toast.error("Please enter your name");
          setBusy(false);
          return;
        }
        const res = await register(form.name, form.email, form.password);
        if (res.pending) {
          setMode("pending");
          setBusy(false);
          return;
        }
        landedUser = res.user;
        toast.success(res.user?.role === "admin" ? "Account created — you're the admin" : "Account created");
      }
      // Site staff signing in on a phone go straight to Field View
      navigate(landingPathFor(landedUser));
    } catch (err) {
      toast.error(errMsg(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* ---------- Brand panel ---------- */}
      <div
        className="noise-overlay relative hidden w-[46%] flex-col justify-between overflow-hidden border-r border-border p-12 lg:flex"
        style={{
          background:
            "radial-gradient(1000px circle at 18% -5%, hsl(var(--primary) / 0.16) 0%, transparent 55%), radial-gradient(700px circle at 90% 110%, hsl(var(--primary) / 0.08) 0%, transparent 60%), hsl(var(--background))",
        }}
      >
        {/* faint architectural set-out grid */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(43,108,176,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(43,108,176,0.07) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 90% 80% at 30% 20%, black 0%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 90% 80% at 30% 20%, black 0%, transparent 75%)",
          }}
        />
        {/* oversized watermark of the mark, echoing an architectural blind-emboss */}
        <img
          aria-hidden="true"
          src="/brand/builtos-mark.svg"
          alt=""
          className="pointer-events-none absolute -bottom-24 -right-40 w-[560px] max-w-none select-none opacity-[0.05] grayscale"
        />

        {/* BuiltOS lockup — the SaaS master brand on public pages */}
        <Link
          to="/welcome"
          className="relative z-10 inline-flex items-center gap-3 self-start rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="BuiltOS home"
          data-testid="auth-brand-logo"
        >
          <BuiltOSMark className="h-12 w-12 drop-shadow-[0_6px_24px_rgba(43,108,176,0.35)]" />
          <Wordmark as="span" size="lg" />
        </Link>

        <div className="relative z-10 max-w-lg">
          <p className="micro-label mb-4 text-primary">Construction command centre</p>
          <h1 className="font-heading text-4xl font-bold leading-[1.15] xl:text-5xl">
            Run every job from
            <br />
            plan to <span className="text-primary">profit.</span>
          </h1>
          <p className="mt-5 max-w-md text-base leading-7 text-muted-foreground">
            AI quantity take-offs, scheduling, purchase orders and job cashflow — built for Australian builders, by
            builders.
          </p>

          <div className="mt-10 space-y-3">
            {VALUE_PROPS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group flex items-start gap-4 rounded-xl border border-transparent px-3 py-2.5 transition-colors duration-200 hover:border-border hover:bg-foreground/[0.03]"
              >
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-accent text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  <Icon className="h-[18px] w-[18px]" />
                </div>
                <div>
                  <p className="text-sm font-semibold tracking-wide">{title}</p>
                  <p className="mt-0.5 text-sm leading-6 text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          {/* proof strip */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border/70 pt-5">
            {PROOF_POINTS.map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </span>
            ))}
          </div>
          <p className="micro-label text-primary/80">Designed for you. Built for life.</p>
        </div>
      </div>

      {/* ---------- Form panel ---------- */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden p-6"
        style={{
          background:
            "radial-gradient(700px circle at 50% 8%, hsl(var(--primary) / 0.07) 0%, transparent 60%), hsl(var(--card) / 0.6)",
        }}
      >
        <div className="relative z-10 w-full max-w-md">
          {/* mobile logo — larger, centred */}
          <div className="mb-8 flex items-center justify-center gap-3 lg:hidden">
            <BuiltOSMark className="h-11 w-11" />
            <Wordmark as="span" size="lg" data-testid="auth-mobile-wordmark" />
          </div>

          <Card className="border-border/80 bg-card/80 shadow-[0_18px_60px_rgba(0,0,0,0.3)] backdrop-blur-xl">
            <CardContent className="pt-7">
              {mode === "pending" ? (
                <div className="py-4 text-center" data-testid="auth-pending-panel">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                    <Hourglass className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="mt-4 font-heading text-2xl font-semibold">Awaiting approval</h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                    Your account has been created and is waiting for an admin to activate it.
                    You'll be able to sign in once it's approved in <span className="text-foreground">Settings → Team</span>.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => setMode("login")}
                    data-testid="auth-pending-back-button"
                  >
                    Back to sign in
                  </Button>
                </div>
              ) : (
              <>
              <p className="micro-label mb-2 text-primary">{mode === "login" ? "Welcome back" : "Get started"}</p>
              <h2 className="font-heading text-2xl font-semibold">
                {mode === "login" ? "Sign in" : "Create your account"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === "login"
                  ? "Enter your details to open the command centre."
                  : "First account created becomes the admin."}
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode === "register" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                      id="name"
                      data-testid="auth-name-input"
                      placeholder="e.g. Sam Carter"
                      value={form.name}
                      onChange={set("name")}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="auth-email-input"
                    placeholder="you@company.com.au"
                    value={form.email}
                    onChange={set("email")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPw ? "text" : "password"}
                      required
                      minLength={6}
                      data-testid="auth-password-input"
                      placeholder="Min 6 characters"
                      value={form.password}
                      onChange={set("password")}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="auth-password-toggle"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full font-semibold tracking-wide shadow-[0_6px_20px_rgba(184,134,75,0.25)]"
                  data-testid={mode === "login" ? "auth-login-submit-button" : "auth-register-submit-button"}
                >
                  {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
                </Button>
              </form>

              <p className="mt-5 text-center text-sm text-muted-foreground">
                {mode === "login" ? "Joining your team's workspace?" : "Already have an account?"}{" "}
                <button
                  type="button"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="font-medium text-primary hover:underline"
                  data-testid="auth-toggle-mode"
                >
                  {mode === "login" ? "Create a team account" : "Log in"}
                </button>
              </p>
              {mode === "login" && (
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  New organisation?{" "}
                  <Link to="/signup" className="font-medium text-primary hover:underline" data-testid="auth-get-started-link">
                    Get started with BuiltOS
                  </Link>
                </p>
              )}
              </>
              )}
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
            Custom homes · Estimates in AUD · GST-aware
          </p>
        </div>
      </div>
    </div>
  );
}
